import React, { ReactNode } from 'react';
import { clsx } from 'clsx';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COLOR_CUSTOMER_REQUEST = '#54DAE2';
const COLOR_WORKFLOW_RULE = '#F9AE00';
const COLOR_KNOWLEDGE_GAP = '#E52020';
const COLOR_OTHER_ESCALATIONS = '#DAE0E2';
const COLOR_AXIS = '#858FA0';

const SERIES = [
  {
    key: 'escalatedUserRequest',
    name: 'Customer request',
    color: COLOR_CUSTOMER_REQUEST,
    dash: '',
  },
  {
    key: 'escalatedModelConfiguration',
    name: 'Workflow rule',
    color: COLOR_WORKFLOW_RULE,
    dash: '',
  },
  { key: 'escalatedKnowledgeGap', name: 'Knowledge gap', color: COLOR_KNOWLEDGE_GAP, dash: '' },
  { key: 'escalatedOther', name: 'Other escalations', color: COLOR_OTHER_ESCALATIONS, dash: '' },
] as const;

export type EscalationTrendsLinePoint = {
  formattedGroup: string;
  escalatedUserRequest?: number;
  escalatedModelConfiguration?: number;
  escalatedKnowledgeGap?: number;
  escalatedOther?: number;
};

type EscalationTrendsLineChartProps = {
  height?: null | number;
  data?: EscalationTrendsLinePoint[];
  title?: string;
  className?: string;
  icon?: ReactNode;
  cartesianGridVisible?: boolean;
  showLegend?: boolean;
};

type TooltipPayloadEntry = { name?: string; value?: number; color?: string };
type CustomTooltipProps = {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
};

const CustomTooltip = ({ active = false, payload = [], label }: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white rounded-sm-5 shadow-tooltip text-xs py-1.5 px-3 flex flex-col gap-1 font-jamjuree">
      {label && <p className="font-medium text-tertiary">{label}</p>}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="w-2.5 h-0.5 rounded-full shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <p className="font-medium">{entry.name}</p>
          <p className="font-bold ml-auto">{entry.value}</p>
        </div>
      ))}
    </div>
  );
};

export const EscalationTrendsLineChart: React.FC<EscalationTrendsLineChartProps> = ({
  data = [],
  title = '',
  height = null,
  className,
  icon = null,
  cartesianGridVisible = false,
  showLegend = true,
}) => {
  return data.length ? (
    <div
      className={clsx(
        'flex flex-col items-start gap-4 p-4 w-full bg-white border border-[#F1F6F6] rounded-sm-5 font-jamjuree',
        className,
      )}
    >
      {title && (
        <div className="flex items-center justify-between w-full">
          <span className="text-sm font-medium leading-[1.5] text-text_secondary">{title}</span>
          {icon}
        </div>
      )}

      <div className="flex flex-col gap-2 w-full">
        <div
          className={'w-full'}
          style={{
            height: height ? `${height}px` : '250px',
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              {cartesianGridVisible && (
                <>
                  <CartesianGrid vertical={false} stroke="#F1F6F6" />
                  {Boolean(data.length) && (
                    <ReferenceLine
                      x={data[parseInt((data.length / 2).toFixed(0)) - 1]['formattedGroup']}
                      stroke="#CCCCCC"
                      strokeDasharray="1 1"
                    />
                  )}
                </>
              )}
              <XAxis
                dataKey="formattedGroup"
                tickLine={false}
                axisLine={{ stroke: '#DFE3E8' }}
                tickMargin={8}
                tick={{
                  fill: COLOR_AXIS,
                  fontSize: 12,
                  fontFamily: 'Bai Jamjuree',
                  fontWeight: 500,
                }}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={{ stroke: '#DFE3E8' }}
                tickMargin={8}
                width={40}
                tick={{
                  fill: COLOR_AXIS,
                  fontSize: 12,
                  fontFamily: 'Bai Jamjuree',
                  fontWeight: 500,
                }}
              />
              <RechartTooltip content={<CustomTooltip />} />
              {SERIES.map((s) => (
                <Line
                  key={s.key}
                  type="linear"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray={s.dash || undefined}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  // isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {showLegend && (
          <div className="flex flex-wrap items-center gap-4 pl-[34px]">
            {SERIES.map((s) => (
              <LegendItem key={s.key} color={s.color} dash={s.dash} label={s.name} />
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;
};

const LegendItem: React.FC<{ color: string; dash: string; label: string }> = ({
  color,
  dash,
  label,
}) => (
  <div className="flex items-center gap-2">
    <svg width="24" height="2" aria-hidden="true">
      <line
        x1="0"
        y1="1"
        x2="24"
        y2="1"
        stroke={color}
        strokeWidth="2"
        strokeDasharray={dash || undefined}
      />
    </svg>
    <span className="text-xs font-medium leading-[1.5] text-tertiary whitespace-nowrap">
      {label}
    </span>
  </div>
);
